import { FieldErrors, Text } from "@certn/cascadia";
import { slugify } from "@certn/utils";
import { css, cx } from "@emotion/css";
import { useContext } from "react";
import Markdown from "react-markdown";

import { BaseLayout } from "./BaseLayout";
import { BaseLayoutContent } from "./BaseLayoutContent";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { RenderError } from "../components/helpers/RenderError";
import { isGroupLayout } from "../utils";
import {
  CascadiaLayoutProps,
  withCascadiaLayoutProps,
} from "../wrappers/withCascadiaLayoutProps";

export function GroupLayout(props: CascadiaLayoutProps) {
  const { uischema, path } = props;
  const { isSummaryForPath, formID } = useContext(CascadiaJsonFormContext);
  const isSummary = isSummaryForPath?.(path);

  const options = props.uischema.options || {};
  if (!isGroupLayout(uischema)) {
    return <RenderError>Invalid Group Layout.</RenderError>;
  }
  const conditionalStyles = !isSummary &&
    options.showDivider && {
      "&:not(:last-child)": css(styles.divider, {
        paddingBottom: "var(--space-4)",
      }),
    };

  return (
    <BaseLayout
      testID={`${slugify(props.label)}-group-layout`}
      {...props}
      className={css(conditionalStyles)}
    >
      <fieldset
        className={css(
          styles.fieldset,
          !isSummary && { gap: "var(--space-4)" },
          options.style === "card" && styles.cardStyle,
          options.colorful && styles.colorful
        )}
      >
        {uischema.label && (
          <legend
            className={css(
              styles.label,
              options.description && { marginBottom: "var(--space-2)" },
              isSummary &&
                !options.showLabelInSummary && {
                  visibility: "hidden",
                  display: "none",
                }
            )}
          >
            <Text
              style={
                options.colorful
                  ? "global/heading-medium"
                  : "form/section-heading"
              }
            >
              {uischema.label}
            </Text>
          </legend>
        )}
        <div
          className={cx(
            "FieldsetContent",
            css([
              styles.fieldset,
              !isSummary && { gap: "var(--space-4)" },
              !isSummary &&
                options.colorful && {
                  marginLeft: "var(--space-4)",
                  marginRight: "var(--space-4)",
                },
            ])
          )}
        >
          {options.subLabel && (
            <Text
              style="form/body-primary"
              color="text-weak"
              data-testid={
                formID
                  ? `${formID}-sub-title`
                  : `${slugify(props.path)}-sub-title`
              }
            >
              {options.subLabel}
            </Text>
          )}
          {!isSummary && options.description && (
            <Markdown
              allowedElements={["strong", "p"]}
              components={{
                p: ({ children }) => (
                  <Text style="form/body-secondary" color="text-weak">
                    {children}
                  </Text>
                ),
              }}
            >
              {options.description}
            </Markdown>
          )}
          <BaseLayoutContent />
          {props.touched && <FieldErrors>{props.errors}</FieldErrors>}
        </div>
      </fieldset>
      {!isSummary && options.footer && (
        <Text style="form/body-secondary" color="text-weak">
          {options.footer}
        </Text>
      )}
    </BaseLayout>
  );
}

const styles = {
  fieldset: css({
    border: "none",
    display: "flex",
    flexDirection: "column",
    padding: 0,
    margin: 0,
    minInlineSize: "auto",
  }),
  label: css({
    padding: 0,
    margin: 0,
    marginBottom: "var(--space-4)",
  }),
  divider: css({
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "var(--border-base)",
    paddingBottom: "var(--space-4)",
  }),
  colorful: css({
    backgroundColor: "var(--surface-sunken)",
    "> legend": {
      backgroundColor: "var(--background-accent-tofino-strong)",
      "> span": {
        color: "var(--text-inverse)",
      },
    },
  }),
  cardStyle: css({
    borderRadius: "var(--border-radius-3)",
    paddingBottom: "var(--space-4)",
    "> .FieldsetContent": {
      gap: "var(--space-0)",
      marginRight: "var(--space-4)",
      marginLeft: "var(--space-4)",
    },
    "> legend": {
      borderTopLeftRadius: "var(--border-radius-3)",
      borderTopRightRadius: "var(--border-radius-3)",
      width: "100%",
      "> span": {
        display: "block",
        margin: "var(--space-4)",
      },
    },
  }),
};

GroupLayout.displayName = "GroupLayout";

export const GroupLayoutRenderer = withCascadiaLayoutProps(GroupLayout);
